import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Container,
    Grid,
    Paper,
    Typography,
    Box,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Pagination,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Card,
    CardContent,
    Divider,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Info as InfoIcon,
    Refresh as RefreshIcon,
    Assessment as AssessmentIcon,
    FilterList as FilterListIcon,
    Clear as ClearIcon,
    Visibility as VisibilityIcon
} from '@mui/icons-material';
import axiosInstance from "../utils/axiosConfig";

const AdminLogs = () => {
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalLogs, setTotalLogs] = useState(0);
    const [filters, setFilters] = useState({
        adminEmail: '',
        action: '',
        targetType: 'all',
        method: 'all',
        startDate: '',
        endDate: ''
    });
    const [showStats, setShowStats] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);

    const targetTypes = ['all', 'user', 'node', 'promo', 'invoice', 'slot', 'warning', 'stats', 'other'];
    const methods = ['all', 'POST', 'PUT', 'PATCH', 'DELETE'];

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const params = {
                page: currentPage,
                limit: 20,
                ...filters
            };

            // Remove empty parameters
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === 'all') {
                    delete params[key];
                }
            });

            const response = await axiosInstance.get('/admin/logs', { params });
            
            setLogs(response.data.logs || []);
            setTotalPages(response.data.pages || 1);
            setTotalLogs(response.data.total || 0);
        } catch (error) {
            console.error('Error loading logs:', error);
            console.error('Error details:', error.response?.data);
            setLogs([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        setStatsLoading(true);
        try {
            const params = {};
            if (filters.startDate) params.startDate = filters.startDate;
            if (filters.endDate) params.endDate = filters.endDate;

            const response = await axiosInstance.get('/admin/logs/stats', { params });
            setStats(response.data);
        } catch (error) {
            console.error('Error loading statistics:', error);
            setStats(null);
        } finally {
            setStatsLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, [currentPage, filters]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setFilters({
            adminEmail: '',
            action: '',
            targetType: 'all',
            method: 'all',
            startDate: '',
            endDate: ''
        });
        setCurrentPage(1);
    };

    const handleRefresh = () => {
        fetchLogs();
        if (showStats) fetchStats();
    };

    const handleLogClick = (log) => {
        setSelectedLog(log);
        setDetailsOpen(true);
    };

    const getStatusColor = (success) => success ? 'success' : 'error';
    
    const getMethodColor = (method) => {
        const colors = { POST: 'success', PUT: 'warning', PATCH: 'info', DELETE: 'error' };
        return colors[method] || 'default';
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const renderChanges = (changes) => {
        if (!changes) return null;

        // Check if changes is in new format (with from/to structure)
        const isNewFormat = Object.values(changes).some(change => 
            change && typeof change === 'object' && change.hasOwnProperty('from') && change.hasOwnProperty('to')
        );

        if (isNewFormat) {
            return (
                <Box>
                    {Object.entries(changes).map(([field, change]) => (
                        <Box key={field} sx={{ mb: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid #dee2e6' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1976d2' }}>
                                {field}:
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                <Box sx={{ 
                                    p: 1, 
                                    bgcolor: '#ffebee', 
                                    borderRadius: 1, 
                                    border: '1px solid #f44336',
                                    fontFamily: 'monospace',
                                    fontSize: '0.85rem'
                                }}>
                                    <Typography component="span" sx={{ color: '#d32f2f', fontWeight: 500 }}>
                                        {typeof change.from === 'object' ? JSON.stringify(change.from) : String(change.from)}
                                    </Typography>
                                </Box>
                                <Typography sx={{ fontWeight: 'bold', color: '#666', mx: 1 }}>→</Typography>
                                <Box sx={{ 
                                    p: 1, 
                                    bgcolor: '#e8f5e8', 
                                    borderRadius: 1, 
                                    border: '1px solid #4caf50',
                                    fontFamily: 'monospace',
                                    fontSize: '0.85rem'
                                }}>
                                    <Typography component="span" sx={{ color: '#2e7d32', fontWeight: 500 }}>
                                        {typeof change.to === 'object' ? JSON.stringify(change.to) : String(change.to)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    ))}
                </Box>
            );
        } else {
            // Fallback to old format (just JSON)
            return (
                <pre style={{ 
                    fontSize: 13, 
                    margin: 0, 
                    whiteSpace: 'pre-wrap',
                    color: '#212529',
                    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                    lineHeight: 1.4
                }}>
                    {JSON.stringify(changes, null, 2)}
                </pre>
            );
        }
    };

    const paperStyle = {
        padding: 3,
        margin: 1
    };

    const statCardStyle = {
        padding: 2,
        textAlign: 'center',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
    };

    return (
        <Container maxWidth="xl">
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" gutterBottom>
                    Admin Logs
                </Typography>
                <Box>
                    <Button
                        variant={showStats ? "contained" : "outlined"}
                        onClick={() => {
                            setShowStats(!showStats);
                            if (!showStats && !stats) fetchStats();
                        }}
                        startIcon={<AssessmentIcon />}
                        sx={{ mr: 2 }}
                    >
                        {showStats ? 'Hide Statistics' : 'Statistics'}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleRefresh}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                    >
                        Refresh
                    </Button>
                </Box>
            </Box>

            {/* Statistics */}
            {showStats && (
                <Paper sx={paperStyle}>
                    <Box sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                            <AssessmentIcon sx={{ mr: 1 }} />
                            Activity Statistics
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        {statsLoading ? (
                            <Box display="flex" justifyContent="center" p={3}>
                                <CircularProgress />
                            </Box>
                        ) : stats ? (
                            <Grid container spacing={3}>
                                <Grid item md={4}>
                                    <Card sx={statCardStyle}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom color="primary">
                                                By Administrators
                                            </Typography>
                                            {stats.byAdmin?.slice(0, 5).map(admin => (
                                                <Box key={admin._id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                                        {admin._id}
                                                    </Typography>
                                                    <Chip label={admin.count} color="primary" size="small" />
                                                </Box>
                                            ))}
                                        </CardContent>
                                    </Card>
                                </Grid>
                                
                                <Grid item md={4}>
                                    <Card sx={statCardStyle}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom color="success.main">
                                                By Action Types
                                            </Typography>
                                            {stats.byAction?.slice(0, 5).map(action => (
                                                <Box key={action._id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                    <Typography variant="body2">
                                                        {action._id}
                                                    </Typography>
                                                    <Chip label={action.count} color="success" size="small" />
                                                </Box>
                                            ))}
                                        </CardContent>
                                    </Card>
                                </Grid>
                                
                                <Grid item md={4}>
                                    <Card sx={statCardStyle}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom color="info.main">
                                                Daily Activity
                                            </Typography>
                                            {stats.dailyActivity?.slice(0, 5).map(day => (
                                                <Box key={day._id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                    <Typography variant="body2">
                                                        {day._id}
                                                    </Typography>
                                                    <Chip label={day.count} color="info" size="small" />
                                                </Box>
                                            ))}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        ) : (
                            <Typography color="warning.main">Failed to load statistics</Typography>
                        )}
                    </Box>
                </Paper>
            )}

            {/* Filters */}
            <Paper sx={paperStyle}>
                <Box sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        <FilterListIcon sx={{ mr: 1 }} />
                        Filters
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Admin Email"
                                value={filters.adminEmail}
                                onChange={(e) => handleFilterChange('adminEmail', e.target.value)}
                                size="small"
                                variant="outlined"
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Type</InputLabel>
                                <Select
                                    value={filters.targetType}
                                    label="Type"
                                    onChange={(e) => handleFilterChange('targetType', e.target.value)}
                                >
                                    {targetTypes.map(type => (
                                        <MenuItem key={type} value={type}>
                                            {type === 'all' ? 'All Types' : type}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Method</InputLabel>
                                <Select
                                    value={filters.method}
                                    label="Method"
                                    onChange={(e) => handleFilterChange('method', e.target.value)}
                                >
                                    {methods.map(method => (
                                        <MenuItem key={method} value={method}>
                                            {method === 'all' ? 'All Methods' : method}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        
                        <Grid item xs={12} md={2}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Date From"
                                value={filters.startDate}
                                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={2}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Date To"
                                value={filters.endDate}
                                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={1}>
                            <Tooltip title="Clear Filters">
                                <IconButton
                                    onClick={handleClearFilters}
                                    color="primary"
                                    size="large"
                                >
                                    <ClearIcon />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>

            {/* Results Information */}
            <Box sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    Found logs: <strong>{totalLogs}</strong>, 
                    page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                </Typography>
            </Box>

            {/* Logs Table */}
            <Paper sx={paperStyle}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Time</TableCell>
                                <TableCell>Administrator</TableCell>
                                <TableCell>Action</TableCell>
                                <TableCell>Method</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>IP</TableCell>
                                <TableCell>Details</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <CircularProgress />
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            Loading logs...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : logs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No logs found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                logs.map(log => (
                                    <TableRow key={log._id} hover>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                                {formatDate(log.timestamp)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {log.adminEmail}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    maxWidth: 200, 
                                                    overflow: 'hidden', 
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                                title={log.action}
                                            >
                                                {log.action}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={log.method} 
                                                color={getMethodColor(log.method)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={log.targetType} 
                                                variant="outlined"
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={log.success ? 'Success' : 'Error'} 
                                                color={getStatusColor(log.success)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                                {log.ipAddress}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="Show Details">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleLogClick(log)}
                                                    color="primary"
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                {totalPages > 1 && (
                    <Box display="flex" justifyContent="center" sx={{ p: 2 }}>
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={(e, page) => setCurrentPage(page)}
                            color="primary"
                            size="large"
                            showFirstButton
                            showLastButton
                        />
                    </Box>
                )}
            </Paper>

            {/* Details Modal */}
            <Dialog 
                open={detailsOpen} 
                onClose={() => setDetailsOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
                    <InfoIcon sx={{ mr: 1 }} />
                    Log Details
                </DialogTitle>
                <DialogContent>
                    {selectedLog && (
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                                    Administrator:
                                </Typography>
                                <Typography variant="body2" gutterBottom sx={{ color: 'text.primary' }}>
                                    {selectedLog.adminEmail}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                                    Time:
                                </Typography>
                                <Typography variant="body2" gutterBottom sx={{ color: 'text.primary' }}>
                                    {formatDate(selectedLog.timestamp)}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                                    Action:
                                </Typography>
                                <Typography variant="body2" gutterBottom sx={{ color: 'text.primary' }}>
                                    {selectedLog.action}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                                    Endpoint:
                                </Typography>
                                <Typography 
                                    variant="body2" 
                                    gutterBottom 
                                    sx={{ 
                                        fontFamily: 'monospace', 
                                        color: '#1976d2',
                                        backgroundColor: '#f5f5f5',
                                        padding: '6px 12px',
                                        borderRadius: 1,
                                        fontSize: '0.9rem',
                                        border: '1px solid #e0e0e0',
                                        wordBreak: 'break-all'
                                    }}
                                >
                                    {selectedLog.endpoint}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                                    IP Address:
                                </Typography>
                                <Typography 
                                    variant="body2" 
                                    gutterBottom 
                                    sx={{ 
                                        fontFamily: 'monospace', 
                                        color: '#1976d2',
                                        backgroundColor: '#f5f5f5',
                                        padding: '6px 12px',
                                        borderRadius: 1,
                                        fontSize: '0.9rem',
                                        border: '1px solid #e0e0e0'
                                    }}
                                >
                                    {selectedLog.ipAddress}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                                    Status:
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    <Chip 
                                        label={selectedLog.success ? 'Success' : 'Error'} 
                                        color={getStatusColor(selectedLog.success)}
                                        size="small"
                                    />
                                </Box>
                            </Grid>
                            
                            {selectedLog.targetEmail && (
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                                        Target Email:
                                    </Typography>
                                    <Typography variant="body2" gutterBottom sx={{ color: 'text.primary' }}>
                                        {selectedLog.targetEmail}
                                    </Typography>
                                </Grid>
                            )}
                            
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                                    User Agent:
                                </Typography>
                                <Paper sx={{ 
                                    p: 2, 
                                    bgcolor: '#f5f5f5', 
                                    mt: 1,
                                    border: '1px solid #e0e0e0'
                                }}>
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            wordBreak: 'break-all', 
                                            fontSize: '0.8rem',
                                            color: '#424242',
                                            fontFamily: 'monospace'
                                        }}
                                    >
                                        {selectedLog.userAgent}
                                    </Typography>
                                </Paper>
                            </Grid>
                            
                            {selectedLog.requestBody && (
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                                        Request Body:
                                    </Typography>
                                    <Paper sx={{ 
                                        p: 2, 
                                        bgcolor: '#f8f9fa', 
                                        mt: 1, 
                                        maxHeight: 200, 
                                        overflow: 'auto',
                                        border: '1px solid #dee2e6'
                                    }}>
                                        <pre style={{ 
                                            fontSize: 13, 
                                            margin: 0, 
                                            whiteSpace: 'pre-wrap',
                                            color: '#212529',
                                            fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                                            lineHeight: 1.4
                                        }}>
                                            {JSON.stringify(selectedLog.requestBody, null, 2)}
                                        </pre>
                                    </Paper>
                                </Grid>
                            )}
                            
                            {selectedLog.changes && (
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                                        Changes:
                                    </Typography>
                                    <Box sx={{ mt: 1, maxHeight: 400, overflow: 'auto' }}>
                                        {renderChanges(selectedLog.changes)}
                                    </Box>
                                </Grid>
                            )}
                            
                            {selectedLog.errorMessage && (
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="error" sx={{ fontWeight: 600 }}>
                                        Error:
                                    </Typography>
                                    <Paper sx={{ 
                                        p: 2, 
                                        bgcolor: '#ffebee', 
                                        mt: 1,
                                        border: '1px solid #f44336'
                                    }}>
                                        <Typography variant="body2" color="error">
                                            {selectedLog.errorMessage}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default AdminLogs; 